import { isAxiosError } from 'axios'
import { promiseTimeout } from '@vueuse/core'
import { notificationStreamUrl, useNotifierApi } from '@/composables/notifier/useNotifierApi'

export type WorkflowLifecycleState =
    | 'STARTED'
    | 'CUSTOM_PAGE_RENDERED'
    | 'CUSTOM_PAGE_UPLOAD_SCANNED'
    | 'COMPLETED'
    | 'FAILED'
    | 'CANCELLED'
    | 'EXPIRED'

export type WorkflowLifecycleEvent = {
    workspaceId: string
    workflowId: string
    state: WorkflowLifecycleState
    version: number
    occurredAt: string
    payload?: {
        layoutId?: string
        objectKey?: string
        isMalicious?: boolean
        scanResultStatus?: string
    }
}

export const TERMINAL_STATES: WorkflowLifecycleState[] = ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED']

export const RECONNECT_DELAYS_MS = [1000, 2000, 4000]

type StreamHandlers = {
    onEvent: (event: WorkflowLifecycleEvent) => void
    onUnauthorized: () => void
    onUnavailable: () => void
    onReconnected: () => void
}

const isUnauthorized = (error: unknown) => isAxiosError(error) && error.response?.status === 401

export const openNotificationStream = ({ onEvent, onUnauthorized, onUnavailable, onReconnected }: StreamHandlers) => {
    const delivered = new Set<string>()
    let source: EventSource | undefined
    let reconnect: ReturnType<typeof setTimeout> | undefined
    let failures = 0
    let connection = 0
    let closed = false

    const deliver = (event: WorkflowLifecycleEvent) => {
        const key = `${event.state}:${event.version}`

        if (delivered.has(key)) {
            return
        }

        delivered.add(key)
        onEvent(event)
    }

    const close = () => {
        closed = true
        clearTimeout(reconnect)
        source?.close()
    }

    const resync = async (current: number, attempt = 0): Promise<void> => {
        const stale = () => closed || current !== connection

        try {
            const { data } = await useNotifierApi().get<WorkflowLifecycleEvent[]>('')

            if (!stale()) {
                data.forEach(deliver)
            }
        } catch (error) {
            if (stale()) {
                return
            }

            if (isUnauthorized(error)) {
                return onUnauthorized()
            }

            if (attempt === RECONNECT_DELAYS_MS.length) {
                close()
                return onUnavailable()
            }

            await promiseTimeout(RECONNECT_DELAYS_MS[attempt])

            return resync(current, attempt + 1)
        }
    }

    const giveUp = async () => {
        close()

        try {
            await useNotifierApi().get('')
        } catch (error) {
            if (isUnauthorized(error)) {
                return onUnauthorized()
            }
        }

        onUnavailable()
    }

    const connect = () => {
        source = new EventSource(notificationStreamUrl())

        source.onopen = async () => {
            const current = ++connection
            const reconnected = failures > 0
            failures = 0

            await resync(current)

            if (reconnected && !closed && current === connection) {
                onReconnected()
            }
        }

        source.addEventListener('notification', message => {
            deliver(JSON.parse((message as MessageEvent<string>).data))
        })

        source.onerror = () => {
            if (closed) {
                return
            }

            source?.close()

            if (failures === RECONNECT_DELAYS_MS.length) {
                giveUp()
                return
            }

            reconnect = setTimeout(connect, RECONNECT_DELAYS_MS[failures++])
        }
    }

    connect()

    return { close }
}
